import React from 'react';
import { useLogger } from '@hooks';
import {
	Box,
	Button,
	ComposableTypography,
	Spacer,
	TextBlock,
	Typography,
	TypographyColorsLevel,
	useTheme,
} from '@bcp-nextgen-dx-component-factory/accolade-design-system';
import { getSessionStorage, rem } from '@helpers';
import { PlanDetail } from '@types';
import { CheckO, StockMarketO } from '@bcp-nextgen-dx-component-factory/accolade-design-system/dist/assets/MBCP/icons';
import { useDidMount, useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { CardHeader } from './card.header';
import { useUserInput, saveUserInput, useServiceResponse, clearUserInput, useIsOperator } from '@store';
import { useDispatch } from 'react-redux';
import { PromoCodeStatus } from '@api/izibizi-xp';
import { Link } from '@components';
import { Image } from '@bcp-nextgen-dx-component-factory/accolade-design-system';
// import { SuccessCircle } from '@bcp-nextgen-dx-component-factory/icons-accolade';
import { Icons } from '@bcp-nextgen-dx-component-factory/assets-mbcp';
import { imagesBcp } from '@assets';

export interface CardPlansProps {
	plans: PlanDetail[];
	hasIntegratedSolution: boolean;
}

export const CardPlan: React.FC<CardPlansProps> = ({ plans, hasIntegratedSolution }) => {
	const logger = useLogger();
	const { palette } = useTheme();
	const t = useTranslation();
	const lastIndex = plans.lastIndexOf(plans[plans.length - 1]);
	const { planId } = useUserInput();
	const dispatch = useDispatch();
	const { retrievePromoCodeDetail } = useServiceResponse();
	const isOperator = useIsOperator();
	const buttonSelect = t('general.select');
	const buttonSelected = t('general.selected');
	const linkFeatures = t('general.features');
	const plansComparisonUrl = t('placeholders.plansComparison.url');

	//TO DEFINE THE CUSTOM Animation value of "CON" promo codes
	const defineCustomPromoAnimation = (i: number) => {
		const codeStatus = retrievePromoCodeDetail?.promoCodeStatus;
		const isSubCodeValid = codeStatus === PromoCodeStatus.ACTIVE ? true : false;

		if (i === 0) {
			// if (retrievePromoCodeDetail && retrievePromoCodeDetail.promoCodeDetail?.code && isSubCodeValid) {
			// 	return false;
			// }
			return retrievePromoCodeDetail?.promoCodeStatus === PromoCodeStatus.ACTIVE;
		} else {
			return retrievePromoCodeDetail?.promoCodeStatus === PromoCodeStatus.ACTIVE || false;
		}
	};
	const handleOnClickSelectPlan = (plan: PlanDetail) => {
		if (planId !== undefined && plan.id !== planId) {
			dispatch(clearUserInput());
		}
		dispatch(saveUserInput({ planId: plan.id }));
	};

	useDidMount(() => {
		if (planId === undefined && plans.length > 0) {
			const passedPlan = getSessionStorage('plan');
			const selectedPlan = plans.find((plan) => plan.id === passedPlan);
			if (selectedPlan !== undefined) {
				handleOnClickSelectPlan(selectedPlan);
			}
		}
	});

	logger('Enter in Card Plan', { plans, planId });

	let color: TypographyColorsLevel = 'linkDefault';
	let colorIcon = palette.primary.p1;
	if (isOperator) {
		color = 'linkDisabled';
		colorIcon = palette.global.ui8;
	}

	return (
		<Box styles={{ gap: '24px', display: 'flex', position: 'relative' }}>
			{plans.map((plan, i) => {
				return (
					<React.Fragment key={plan.id}>
						<Box
							styles={{
								flexDirection: 'column',
								alignItems: 'center',
								alignSelf: 'start',
								width: rem(386),
								height: 'auto',
								background: palette.global.ui18,
								boxShadow:
									plan.id === planId
										? '0px 12px 20px rgba(0, 0, 0, 0.2)'
										: '0px 8px 16px rgba(0, 0, 0, 0.1)',
								borderRadius: '16px',
							}}
						>
							<Box
								styles={{
									height: rem(230),
									width: '100%',
									flexDirection: 'column',
								}}
							>
								<CardHeader
									plan={plan}
									opacity={plan.name === 'Standard' ? '0.7' : '1'}
									hasIntegratedSolution={hasIntegratedSolution}
									// useCode={
									// 	retrievePromoCodeInformation?.promoCodeStatus ===
									// 		GetPromoCodeInformationOutPromoCodeStatus.ACTIVE || false
									// }
									useCode={defineCustomPromoAnimation(i)}
								/>
							</Box>
							<Box
								styles={{
									flexDirection: 'column',
									height: '100%',
									gap: '16px',
									width: '354px',
								}}
							>
								{plan.planOffers.map((offer, index) => (
									<Box key={index} styles={{ flexDirection: 'row', gap: '16px' }}>
										<Image source={imagesBcp.successCircle} />
										<Box styles={{ flexDirection: 'column' }}>
											<ComposableTypography
												baseProps={{ appearance: 'body-100-02' }}
												text={offer.title}
												tags={{
													b: { appearance: 'body-100-04' },
												}}
											/>
											<Typography appearance="body-75-02" colorToken="secondary-light">
												{offer.subtitle}
											</Typography>
										</Box>
									</Box>
								))}
							</Box>
							<Box styles={{ position: 'relative', margin: '48px 0px' }}>
								{plan.id !== planId ? (
									<Button
										size="large"
										appearance="primary"
										disabled={isOperator}
										onPress={() => handleOnClickSelectPlan(plan)}
									>
										{buttonSelect}
									</Button>
								) : (
									<Box
										styles={{
											alignItems: 'center',
											gap: '8px',
											height: '48px',
										}}
									>
										<Image source={imagesBcp.check} />
										<Typography appearance="body-100-04" colorsLevel={color}>
											{buttonSelected}
										</Typography>
									</Box>
								)}
								{/* commented for US 768061 */}
								{/* <Box>
									<Link onClick={() => window.open(plansComparisonUrl, '_blank')}>
										<Box styles={{ alignItems: 'center' }}>
											<Typography
												appearance="body-75-03"
												alternativeStyleProperties={{ color: palette.global.ui9 }}
											>
												{linkFeatures}
											</Typography>
											<Spacer horizontal={rem(4)} />
											<StockMarketO fill={palette.global.ui9} width={rem(24)} height={rem(24)} />
										</Box>
									</Link>
								</Box> */}
							</Box>
							<Box
								styles={{
									flexDirection: 'column',
									gap: '4px',
									alignItems: 'end',
								}}
							>
								{plan.planExtras.map((extras, index) => (
									<Box
										key={index}
										styles={{
											flexDirection: 'row',
											alignItems: 'center',
											backgroundColor: '#F3F4F6',
											height: '52px',
											width: '354px',
											borderRadius: '8px',
											padding: '16px',
											// gap: '24px',
											justifyContent: 'space-between',
										}}
									>
										<Box styles={{ flexDirection: 'column' }}>
											{extras.value === undefined ? (
												<Typography appearance="body-75-02" colorToken="secondary-light">
													<s>{extras.description}</s>
												</Typography>
											) : (
												<Typography appearance="body-75-03">{extras.description}</Typography>
											)}

											<Typography appearance="microcopy-75-02" colorToken="secondary-light">
												{extras.subDescription}
											</Typography>
										</Box>
										<ComposableTypography
											baseProps={{ appearance: 'microcopy-100-02' }}
											text={extras.value as string}
											tags={{
												b: { appearance: 'body-100-03' },
												div: { appearance: 'microcopy-100-02', colorToken: 'secondary-light' },
											}}
										/>
									</Box>
								))}
							</Box>
							<Box styles={{ marginTop: '16px', marginBottom: '40px' }}>
								<Typography appearance="microcopy-75-02" colorToken="secondary-light">
									{plan.disclaimer}
								</Typography>
							</Box>
						</Box>
						{/* {plans.length > 1 && lastIndex !== i && <Spacer horizontal={rem(20)} />} */}
					</React.Fragment>
				);
			})}
		</Box>
	);
};
